/**
 * CENTRAL IMAGE REGISTRY
 * ----------------------
 * All site image slots are listed here. Update the `src` to swap an image.
 *
 * Image files live in /public/images/ and are referenced with absolute paths
 * starting with "/images/...".
 *
 * Leave `src: ''` to show a labeled placeholder helpful for design review.
 *
 * See /public/images/IMAGES.md for the full shot list documentation.
 */

export type ImageRef = {
  src: string;
  alt: string;
  description: string;
  dimensions: string;
};

type Registry = Record<string, ImageRef>;

export const images: Registry = {
  // ===== HOME =====
  'home.hero': {
    src: '/images/hero-aerial.jpg',
    alt: 'Aerial view at dawn of the Caribbean coast where the rainforest meets the sea at Nativa Living.',
    description: 'Hero: moody dawn aerial of river meeting sea, mountains behind.',
    dimensions: '2400 x 1800',
  },
  'home.why-now': {
    src: '/images/palm-beach.jpg',
    alt: 'A coconut palm leaning over the white sand of a Caribbean beach near Nativa Living.',
    description: 'Iconic palm over the Caribbean shoreline.',
    dimensions: '1800 x 1350',
  },
  'home.land-overview': {
    src: '/images/coast-river-aerial.jpg',
    alt: 'Aerial of 160 hectares of forest meeting the Caribbean Sea, with a river mouth in the foreground.',
    description: 'Wide aerial showing scale of the property.',
    dimensions: '2400 x 1800',
  },
  'home.tier.village': {
    src: '/images/white-sand-beach.jpg',
    alt: 'Empty white sand beach with palms and the Caribbean Sea, a short walk from the village tier lots.',
    description: 'Village tier: walkable, near the beach.',
    dimensions: '1500 x 2000',
  },
  'home.tier.garden': {
    src: '/images/home-infinity-pool.jpg',
    alt: 'Modern garden home with an infinity pool overlooking the Caribbean at sunset.',
    description: 'Garden tier: architectural rendering of a Costa Nativa home.',
    dimensions: '2200 x 1240',
  },
  'home.tier.estate': {
    src: '/images/sloth-canopy.jpg',
    alt: 'Three-toed sloth resting in the canopy of an old-growth tree at Nativa Living.',
    description: 'Estate tier: privacy, forest immersion.',
    dimensions: '1400 x 1860',
  },
  'home.tier.signature': {
    src: '/images/coast-dawn-aerial.jpg',
    alt: 'Dramatic dawn aerial of the Caribbean coast with mountains rising behind the rainforest.',
    description: 'Signature tier: trophy ridgeline / sweeping coastline.',
    dimensions: '2400 x 1800',
  },
  'home.vision': {
    src: '/images/howler-monkeys.jpg',
    alt: 'Howler monkeys moving through the rainforest canopy above the property.',
    description: 'Wildlife in the canopy: the vision in nature.',
    dimensions: '1600 x 1070',
  },
  'home.closing': {
    src: '/images/forest-to-ocean-aerial.jpg',
    alt: 'Aerial of unbroken rainforest reaching the Caribbean Sea at Nativa Living.',
    description: 'Closing CTA: aerial of forest to ocean.',
    dimensions: '1800 x 1350',
  },

  // ===== LAND =====
  'land.hero': {
    src: '/images/coast-dawn-aerial.jpg',
    alt: 'Aerial of the river meeting the Caribbean at dawn with the Talamanca range in the distance.',
    description: 'Land hero: river-to-sea aerial.',
    dimensions: '2400 x 1800',
  },
  'land.geo.hectares': {
    src: '/images/forest-to-ocean-aerial.jpg',
    alt: 'Aerial view emphasizing the 160 contiguous hectares of forest meeting the Caribbean.',
    description: 'Wide aerial showing scale of contiguous hectares.',
    dimensions: '1800 x 1350',
  },
  'land.geo.river': {
    src: '/images/river-meets-sea.jpg',
    alt: 'A river flowing through coastal forest and meeting the Caribbean Sea, viewed from above.',
    description: 'River from mountain to sea, aerial.',
    dimensions: '1800 x 1350',
  },
  'land.geo.beach': {
    src: '/images/white-sand-beach.jpg',
    alt: 'White sand beach with calm turquoise waves and palm-lined shoreline at the river mouth.',
    description: 'Beach concession at the river mouth.',
    dimensions: '1500 x 2000',
  },
  'land.wildlife': {
    src: '/images/macaw-flight.jpg',
    alt: 'A great green macaw in flight above the rainforest canopy.',
    description: 'Great green macaw in flight, vivid wildlife shot.',
    dimensions: '1600 x 1070',
  },
  'land.culture': {
    src: '/images/heliconia-red.jpg',
    alt: 'Vibrant red heliconia flower in the Costa Rican rainforest understory.',
    description: 'Red heliconia: vivid native flora.',
    dimensions: '1400 x 1860',
  },
  'land.forest': {
    src: '/images/sloth-canopy.jpg',
    alt: 'Three-toed sloth resting in the dense canopy of the rainforest.',
    description: 'Sloth in canopy: old-growth forest.',
    dimensions: '1400 x 1860',
  },
  'land.infrastructure': {
    src: '/images/route-36-aerial.jpg',
    alt: 'Aerial of the freshly paved Route 36 corridor running along the Caribbean shoreline.',
    description: 'Route 36 coastal corridor: access infrastructure.',
    dimensions: '1800 x 1350',
  },
  'land.river-bamboo': {
    src: '/images/river-bamboo.jpg',
    alt: 'A quiet stretch of river lined with arching bamboo on the property.',
    description: 'River and bamboo, walking scale.',
    dimensions: '1800 x 1200',
  },
  'land.sea-cliff': {
    src: '/images/sea-cave-cliff.jpg',
    alt: 'Rocky sea cliff with caves where the forest meets the Caribbean.',
    description: 'Sea cliff and forest meeting the ocean.',
    dimensions: '1800 x 1200',
  },
  'land.toucan': {
    src: '/images/toucan-canopy.jpg',
    alt: 'A toucan perched in the high canopy of a rainforest tree at Nativa Living.',
    description: 'Toucan in canopy.',
    dimensions: '1600 x 1070',
  },
  'land.heliconia': {
    src: '/images/heliconia-red.jpg',
    alt: 'Bright red heliconia flower in the understory of the Costa Rican rainforest.',
    description: 'Red heliconia close up.',
    dimensions: '1400 x 1860',
  },

  // ===== VISION =====
  'vision.hero': {
    src: '/images/brand-sign-jungle.jpg',
    alt: 'Costa Nativa luxury eco real estate sign set inside lush jungle planting.',
    description: 'Brand sign in jungle: the vision made real.',
    dimensions: '2000 x 1500',
  },
  'vision.principle.river': {
    src: '/images/river-bamboo.jpg',
    alt: 'River pooling between mossy banks under arching bamboo and rainforest canopy.',
    description: 'River decides: quiet bamboo-lined river.',
    dimensions: '1800 x 1200',
  },
  'vision.principle.forest': {
    src: '/images/sea-cave-cliff.jpg',
    alt: 'Forested sea cliff where the jungle meets the Caribbean.',
    description: 'Forest stays: where jungle meets the sea.',
    dimensions: '1800 x 1200',
  },
  'vision.principle.people': {
    src: '/images/sloth-leaves.jpg',
    alt: 'Three-toed sloth tucked into palm fronds in the protected forest understory.',
    description: 'The place has people: quiet native life.',
    dimensions: '1400 x 1860',
  },
  'vision.promise': {
    src: '/images/palm-shipwreck.jpg',
    alt: 'A leaning palm and a wrecked boat resting in shallow Caribbean surf.',
    description: 'Promise: legacy, time, quiet permanence.',
    dimensions: '1800 x 1200',
  },

  // ===== LOTS =====
  'lots.hero': {
    src: '/images/river-meets-sea.jpg',
    alt: 'Aerial view of the masterplan area where the river meets the ocean.',
    description: 'Masterplan / village-scale aerial.',
    dimensions: '1800 x 1350',
  },
  'lots.village': {
    src: '/images/palm-beach.jpg',
    alt: 'A coconut palm framing a quiet stretch of Caribbean beach near the village lots.',
    description: 'Village tier: walkable, near the action.',
    dimensions: '1800 x 1350',
  },
  'lots.garden': {
    src: '/images/home-infinity-pool.jpg',
    alt: 'Garden tier home with an infinity pool and tropical landscaping above the Caribbean.',
    description: 'Garden tier: private pool, outdoor rooms.',
    dimensions: '2200 x 1240',
  },
  'lots.estate': {
    src: '/images/brand-sign-jungle.jpg',
    alt: 'Estate tier signage tucked into the deep rainforest at Nativa Living.',
    description: 'Estate tier: forest immersion, privacy.',
    dimensions: '2000 x 1500',
  },
  'lots.signature': {
    src: '/images/coast-dawn-aerial.jpg',
    alt: 'Signature tier ridgeline location overlooking the Caribbean coast at dawn.',
    description: 'Signature tier: trophy view.',
    dimensions: '2400 x 1800',
  },
  'lots.amenity.beach': {
    src: '/images/white-sand-beach.jpg',
    alt: 'Owners-only beach club access at the river mouth on the Caribbean.',
    description: 'Beach club amenity.',
    dimensions: '1500 x 2000',
  },
  'lots.amenity.wellness': {
    src: '/images/river-bamboo.jpg',
    alt: 'A still bamboo-lined river setting for the wellness program at Nativa Living.',
    description: 'Wellness: slow, natural pace.',
    dimensions: '1800 x 1200',
  },
  'lots.amenity.trails': {
    src: '/images/forest-to-ocean-aerial.jpg',
    alt: 'Marked walking trails running the river spine of the property to the sea.',
    description: 'River-to-sea trails.',
    dimensions: '1800 x 1350',
  },
  'lots.amenity.cacao': {
    src: '/images/heliconia-red.jpg',
    alt: 'Vibrant tropical flora supporting the cacao orchard ecosystem.',
    description: 'Cacao and farm-to-table.',
    dimensions: '1400 x 1860',
  },
  'lots.amenity.security': {
    src: '/images/village-beach-aerial.jpg',
    alt: 'Aerial of the gated village area showing private entries and 24/7 security.',
    description: 'Security and gated entries.',
    dimensions: '1800 x 1350',
  },
  'lots.amenity.concierge': {
    src: '/images/home-infinity-pool.jpg',
    alt: 'A Nativa Living home maintained by the on-site stewardship and concierge program.',
    description: 'Stewardship and concierge.',
    dimensions: '2200 x 1240',
  },

  // ===== SHARED =====
  'cta.closing': {
    src: '/images/coast-dawn-aerial.jpg',
    alt: 'Coastline at dawn where the river meets the Caribbean Sea.',
    description: 'Reusable closing CTA background.',
    dimensions: '2400 x 1800',
  },
};

export function getImage(id: string): ImageRef {
  const ref = images[id];
  if (!ref) {
    return {
      src: '',
      alt: 'Missing image',
      description: `Unknown slot: ${id}`,
      dimensions: 'unknown',
    };
  }
  return ref;
}
